import React, { useContext, useState, useEffect } from 'react';
import {
    DatabaseBackup,
    ShieldAlert,
    Download,
    Trash2,
    Clock,
    Lock,
    CheckCircle2,
    Loader2
} from 'lucide-react';

import styles from './styles.module.css';
import { Context } from '../../context/UserContext';
import useFlashMessage from '../../hooks/useFlashMessage';
import api from '../../utils/api';

// ─────────────────────────────────────────────
// JANELA DE BACKUP
// Abre todo dia 20 de janeiro
// Fecha dia 4 de fevereiro
// Após isso notas com mais de 3 anos podem ser apagadas
// ─────────────────────────────────────────────
function getBackupWindowStatus() {

    const now = new Date();
    const year = now.getFullYear();

    const windowStart = new Date(year, 0, 20); // 20 jan
    const windowEnd = new Date(year, 1, 4); // 4 fev
    const deleteDate = new Date(year, 1, 5); // 5 fev

    const isInWindow =
        now >= windowStart &&
        now <= windowEnd;

    const daysLeft = isInWindow
        ? Math.ceil(
            (windowEnd - now) /
            (1000 * 60 * 60 * 24)
        )
        : 0;

    return {
        isInWindow,
        daysLeft,
        windowStart,
        windowEnd,
        deleteDate,
        year
    };
}

export default function DataBackup() {

    const { user } = useContext(Context);
    const { setFlashMessage } = useFlashMessage();

    const [status, setStatus] =
        useState(getBackupWindowStatus());

    const [downloading, setDownloading] =
        useState(false);

    const [done, setDone] =
        useState(false);

    // Atualiza contador
    useEffect(() => {

        const timer = setInterval(() => {

            setStatus(getBackupWindowStatus());

        }, 60000);

        return () => clearInterval(timer);

    }, []);

    // ─────────────────────────────────────────────
    // PLANO FREE
    // ─────────────────────────────────────────────
    if (!user || user.plan === 'free') {

        return (
            <div className={styles.container}>

                <div className={styles.upgradeBox}>

                    <div className={styles.upgradeIcon}>
                        <Lock size={28} />
                    </div>

                    <h3 className={styles.upgradeTitle}>
                        Backup de Dados
                    </h3>

                    <p className={styles.upgradeText}>
                        Usuários do plano free não possuem
                        backup automático das notas fiscais.
                        Após 3 anos os dados antigos podem
                        ser removidos permanentemente.
                    </p>

                    <div className={styles.upgradeBadge}>
                        <ShieldAlert size={13} />
                        Disponível apenas nos planos Plus e Premium
                    </div>

                </div>

            </div>
        );
    }

    // ─────────────────────────────────────────────
    // FORA DA JANELA
    // ─────────────────────────────────────────────
    if (!status.isInWindow) {

        const nextWindow = new Date(
            new Date().getFullYear() +
            (new Date().getMonth() >= 1 ? 1 : 0),
            0,
            20
        );

        const daysUntilNext = Math.ceil(
            (nextWindow - new Date()) /
            (1000 * 60 * 60 * 24)
        );

        return (

            <div className={styles.container}>

                <div className={styles.infoBox}>

                    <div className={styles.infoIcon}>
                        <DatabaseBackup size={26} />
                    </div>

                    <div className={styles.infoContent}>

                        <h3 className={styles.infoTitle}>
                            Backup de Dados
                        </h3>

                        <p className={styles.infoText}>
                            A janela anual de backup abre
                            todo ano no dia <strong>20 de janeiro</strong>.

                            Durante esse período você poderá baixar
                            todas as suas notas fiscais armazenadas
                            dos últimos <strong>3 anos</strong>.
                        </p>

                        <div className={styles.countdown}>
                            <Clock size={14} />
                            Próxima janela em <strong>{daysUntilNext} dias</strong>
                        </div>

                    </div>

                </div>

                <div className={styles.timeline}>

                    <div className={styles.timelineItem}>

                        <span className={styles.timelineDot} />

                        <div>

                            <p className={styles.timelineLabel}>
                                Janela de backup abre
                            </p>

                            <p className={styles.timelineDate}>
                                20 de janeiro de {
                                    new Date().getFullYear() +
                                    (new Date().getMonth() >= 1 ? 1 : 0)
                                }
                            </p>

                        </div>

                    </div>

                    <div className={styles.timelineLine} />

                    <div className={styles.timelineItem}>

                        <span className={`${styles.timelineDot} ${styles.timelineDotRed}`} />

                        <div>

                            <p className={styles.timelineLabel}>
                                Limpeza automática de notas antigas
                            </p>

                            <p className={styles.timelineDate}>
                                5 de fevereiro de {
                                    new Date().getFullYear() +
                                    (new Date().getMonth() >= 1 ? 1 : 0)
                                }
                            </p>

                        </div>

                    </div>

                </div>

            </div>
        );
    }

    // ─────────────────────────────────────────────
    // DOWNLOAD BACKUP
    // ─────────────────────────────────────────────
    async function handleDownload() {

        setDownloading(true);

        try {

            const token = JSON.parse(
                localStorage.getItem('token')
            );

            const response = await api.get(
                '/invoices/backup',
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    responseType: 'blob',
                }
            );

            const url = window.URL.createObjectURL(
                new Blob([response.data])
            );

            const link = document.createElement('a');

            link.href = url;

            link.setAttribute(
                'download',
                `backup-notas-${status.year}.json`
            );

            document.body.appendChild(link);

            link.click();

            link.remove();

            window.URL.revokeObjectURL(url);

            setDone(true);

            setFlashMessage(
                'Backup realizado com sucesso!',
                'success'
            );

        } catch (error) {

            setFlashMessage(
                'Erro ao gerar backup.',
                'error'
            );

        } finally {

            setDownloading(false);

        }
    }

    // ─────────────────────────────────────────────
    // DENTRO DA JANELA
    // ─────────────────────────────────────────────
    return (

        <div className={styles.container}>

            {/* ALERTA */}
            <div className={styles.alertBanner}>

                <ShieldAlert size={16} />

                <span>
                    As notas fiscais com mais de <strong>3 anos</strong>
                    poderão ser removidas em{' '}
                    <strong>
                        {status.daysLeft} {
                            status.daysLeft === 1
                                ? 'dia'
                                : 'dias'
                        }
                    </strong>.
                    Faça seu backup agora.
                </span>

            </div>

            <div className={styles.activeBox}>

                <div className={styles.activeHeader}>

                    <div className={styles.activeIcon}>
                        <DatabaseBackup size={24} />
                    </div>

                    <div>

                        <h3 className={styles.activeTitle}>
                            Janela de Backup Aberta
                        </h3>

                        <p className={styles.activeSubtitle}>
                            Baixe todas as suas notas fiscais
                            armazenadas dos últimos 3 anos
                            em formato JSON.
                        </p>

                    </div>

                </div>

                {/* BARRA */}
                <div className={styles.urgencyBar}>

                    <div className={styles.urgencyTrack}>

                        <div
                            className={styles.urgencyFill}
                            style={{
                                width: `${((15 - status.daysLeft) / 15) * 100}%`
                            }}
                        />

                    </div>

                    <span className={styles.urgencyLabel}>
                        {status.daysLeft} {
                            status.daysLeft === 1
                                ? 'dia restante'
                                : 'dias restantes'
                        }
                    </span>

                </div>

                {/* EXCLUSÃO */}
                <div className={styles.deleteWarning}>

                    <Trash2 size={14} />

                    <span>

                        Limpeza automática programada para{' '}

                        <strong>
                            {
                                status.deleteDate.toLocaleDateString(
                                    'pt-BR',
                                    {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric'
                                    }
                                )
                            }
                        </strong>

                    </span>

                </div>

                {/* BOTÃO */}
                {
                    done ? (

                        <div className={styles.doneBox}>

                            <CheckCircle2 size={18} />

                            Backup baixado com sucesso!
                            Guarde o arquivo em local seguro.

                        </div>

                    ) : (

                        <button
                            className={styles.downloadBtn}
                            onClick={handleDownload}
                            disabled={downloading}
                        >

                            {
                                downloading ? (
                                    <>
                                        <Loader2
                                            size={16}
                                            className={styles.spinner}
                                        />
                                        Gerando backup...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} />
                                        Baixar backup
                                    </>
                                )
                            }

                        </button>

                    )
                }

            </div>

        </div>
    );
}